/**
 * Utility functions for the SplashScreen component.
 *
 * These helpers cover the splash screen's pure logic: computing proportional
 * phase timings, clamping the configurable duration, deriving the typing-effect
 * character count, and reading/writing the sessionStorage flags that gate
 * whether the splash should be shown. The sessionStorage helpers are guarded
 * with try/catch so the splash degrades gracefully when storage is unavailable
 * (e.g. private-browsing restrictions).
 */

import { AnimationTimings } from './types'

/** Tagline rendered with the typing effect during the animate phase. */
const TAGLINE = 'Hệ thống quản lý bán hàng chuyên nghiệp'

/** Typing speed for the tagline, in characters per second. */
const CHARS_PER_SECOND = 30

/** Minimum fade-out duration (ms). Always reserved from the total duration. */
const FADE_OUT_DURATION = 300

/** sessionStorage key set by the login handler right after authentication. */
const KEY_JUST_LOGGED_IN = 'skl_just_logged_in'

/** sessionStorage key marking that the splash has already been shown this tab session. */
const KEY_SPLASH_SHOWN = 'skl_splash_shown'

/**
 * Calculate animation phase timings based on the total duration.
 *
 * The fade-out duration is always reserved at {@link FADE_OUT_DURATION} ms and
 * the remaining time is split proportionally across the other phases
 * (fade-in ~20%, animate ~40%, progress ~28% of the total). The progress phase
 * absorbs any rounding remainder so the four phases always sum to
 * `totalDuration`.
 *
 * @param totalDuration - Total animation duration in ms (expected pre-clamped).
 * @returns Resolved per-phase durations in ms.
 */
export function calculateTimings(totalDuration: number): AnimationTimings {
  const fadeOut = FADE_OUT_DURATION
  const remaining = totalDuration - fadeOut
  const fadeIn = Math.round(remaining * 0.227) // ~500ms at 2500 total
  const animate = Math.round(remaining * 0.455) // ~1000ms at 2500 total
  const progress = remaining - fadeIn - animate // ~700ms at 2500 total

  return { fadeIn, animate, progress, fadeOut }
}

/**
 * Clamp a numeric value to an inclusive `[min, max]` range.
 *
 * @param value - The value to clamp.
 * @param min - Lower bound (default 500).
 * @param max - Upper bound (default 10000).
 * @returns The value constrained to `[min, max]`.
 */
export function clampDuration(value: number, min = 500, max = 10000): number {
  return Math.min(max, Math.max(min, value))
}

/**
 * Calculate the number of tagline characters that should be visible at a given
 * point in the typing phase.
 *
 * Characters appear at {@link CHARS_PER_SECOND} chars/sec and the count is
 * capped at the tagline length, yielding `min(floor(t * 30 / 1000), length)`.
 *
 * @param elapsedInPhase - ms elapsed since the typing phase started.
 * @returns Number of characters that should be visible (monotonically non-decreasing).
 */
export function getTypedCharCount(elapsedInPhase: number): number {
  const totalChars = TAGLINE.length
  const expectedChars = Math.floor((elapsedInPhase / 1000) * CHARS_PER_SECOND)
  return Math.min(expectedChars, totalChars)
}

/**
 * Determine whether the splash screen should be shown based on sessionStorage.
 *
 * Returns `true` only when the user just logged in
 * (`skl_just_logged_in === 'true'`) and the splash has not yet been shown this
 * tab session (`skl_splash_shown` absent). Any storage access error (e.g.
 * private browsing) results in `false` so the app proceeds without a splash.
 *
 * @returns `true` if the splash should display, otherwise `false`.
 */
export function shouldShowSplash(): boolean {
  try {
    const justLoggedIn = sessionStorage.getItem(KEY_JUST_LOGGED_IN)
    const alreadyShown = sessionStorage.getItem(KEY_SPLASH_SHOWN)
    return justLoggedIn === 'true' && alreadyShown === null
  } catch {
    // sessionStorage unavailable (e.g. private browsing restrictions).
    return false
  }
}

/**
 * Mark the splash as complete in sessionStorage.
 *
 * Sets `skl_splash_shown` to `'true'` and removes the `skl_just_logged_in`
 * flag. Fails silently if sessionStorage is unavailable, in which case the
 * splash may show again on the next refresh (acceptable degradation).
 */
export function markSplashComplete(): void {
  try {
    sessionStorage.setItem(KEY_SPLASH_SHOWN, 'true')
    sessionStorage.removeItem(KEY_JUST_LOGGED_IN)
  } catch {
    // Silently fail if sessionStorage is unavailable.
  }
}
